import React, { useEffect, useState, useMemo, Fragment } from 'react';
import Layouts from '../../layouts/Layouts';
import { http, getAuthorizationHeader } from '../../config/http';
import { toast } from 'react-toastify';
import moment from 'moment';
import { DateRange } from 'react-date-range';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
    IconCalendarTime,
    IconReload,
    IconCash,
    IconQrcode,
    IconBuildingBank,
    IconReceipt2,
    IconCreditCard,
    IconArrowsSplit,
    IconSearch,
    IconChevronLeft,
    IconChevronRight,
    IconFilter,
    IconChartPie,
    IconArrowsRightLeft,
    IconCheck,
    IconX,
    IconExternalLink,
    IconEdit
} from '@tabler/icons-react';
import { DinamicTitle } from '../../utils/DinamicTitle';
import ModalKoreksiPembayaran from '../../components/ModalKoreksiPembayaran';

function numberWithCommas(x) {
    if (x === null || x === undefined || x === '') return '0';
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function checkBerat(number) {
    if (number === null || number === undefined || number === '') return '0';
    const beratConfirm = parseFloat(number);
    return beratConfirm % 1 === 0
        ? beratConfirm.toString()
        : beratConfirm.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
}

export default function LaporanPembayaran() {
    DinamicTitle('Laporan Pembayaran || Komaladewi');
    const navigate = useNavigate();

    const role = localStorage.getItem('role');

    // Tabs: 'rekap' | 'split'
    const [activeTab, setActiveTab] = useState('rekap');

    const [summary, setSummary] = useState(null);
    const [splitData, setSplitData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filters
    const [cabangFilter, setCabangFilter] = useState('');
    const [cabangList, setCabangList] = useState([]);

    // Date filter (default: today)
    const [startDate, setStartDate] = useState(() => new Date());
    const [endDate, setEndDate] = useState(() => new Date());

    // Split Tab specific filters & pagination
    const [searchKeyword, setSearchKeyword] = useState('');
    const [metodeFilter, setMetodeFilter] = useState('all'); // all, cash, qris, transfer, edc
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [selectedSplitTrx, setSelectedSplitTrx] = useState(null);
    const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
    const [isKoreksiPaymentOpen, setIsKoreksiPaymentOpen] = useState(false);

    // State Modal Proses ke Cashflow (Pengeluaran Luar)
    const [isOpenProsesModal, setIsOpenProsesModal] = useState(false);
    const [prosesCabangId, setProsesCabangId] = useState('');
    const [loadingProses, setLoadingProses] = useState(false);
    const [existingCashflow, setExistingCashflow] = useState({
        transfer: null,
        qris: null,
        edc: null,
    });
    const [prosesSelection, setProsesSelection] = useState({
        transfer: true,
        qris: true,
        edc: true,
    });
    const [prosesKeterangan, setProsesKeterangan] = useState({
        transfer: '',
        qris: '',
        edc: '',
    });

    const selectionRange = {
        startDate: startDate || new Date(),
        endDate: endDate || new Date(),
        key: 'selection',
    };

    const handleSelectDate = (date) => {
        setStartDate(date.selection.startDate);
        setEndDate(date.selection.endDate);
        setCurrentPage(1);
    };

    const handleResetDate = () => {
        setStartDate(null);
        setEndDate(null);
        setCurrentPage(1);
    };

    // Klik ikon refresh: reset filter tanggal ke hari ini (default date) & muat ulang data
    const handleRefreshToday = () => {
        const today = new Date();
        setStartDate(today);
        setEndDate(today);
        setCurrentPage(1);
    };

    const awal = startDate ? moment(startDate).format('DD-MM-YYYY') : '';
    const akhir = endDate ? moment(endDate).format('DD-MM-YYYY') : '';

    useEffect(() => {
        if (role === 'owner' || role === 'admin') {
            http.get(`/cabang`, {
                headers: { Authorization: getAuthorizationHeader() },
            }).then(res => {
                const stores = res.data.data.filter(c => c.is_toko === 1 || c.is_toko === '1' || c.is_toko === true);
                setCabangList(stores);
            }).catch(() => {});
        }
    }, [role]);

    useEffect(() => {
        fetchData();
        checkExistingCashflow(cabangFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cabangFilter, startDate, endDate]);

    const getDigitalMethodKey = (tipe) => {
        const t = (tipe || '').toLowerCase();
        if (t === 'digitalpay-transfer' || t === 'digitalpay transfer' || t === 'digital pay transfer') return 'transfer';
        if (t === 'digitalpay-qris' || t === 'digitalpay qris' || t === 'digital pay qris') return 'qris';
        if (
            t === 'digitalpay-edc-debit-kredit' ||
            t === 'digitalpay-edc' ||
            t === 'digitalpay edc/debit/kredit' ||
            t === 'digital pay edc'
        ) {
            return 'edc';
        }
        return null;
    };

    const fetchExistingCashflowMap = async (targetCabangId = cabangFilter) => {
        const tglAwalCf = startDate ? moment(startDate).format('DD-MM-YYYY') : moment().format('DD-MM-YYYY');
        const tglAkhirCf = endDate ? moment(endDate).format('DD-MM-YYYY') : moment().format('DD-MM-YYYY');
        const cabQuery = targetCabangId ? `&cabang=${targetCabangId}` : '';

        const fetchAllPagesByStatus = async (statusFlag) => {
            const firstRes = await http
                .get(`/cashflow?page=1&tgl_awal=${tglAwalCf}&tgl_akhir=${tglAkhirCf}${cabQuery}&is_proses=${statusFlag}`, {
                    headers: { Authorization: getAuthorizationHeader() },
                })
                .catch(() => ({ data: { data: { data: [], last_page: 1 } } }));

            const firstPageData = firstRes?.data?.data?.data || [];
            const lastPage = Number(firstRes?.data?.data?.last_page || 1);
            if (lastPage <= 1) return firstPageData;

            const extraPromises = [];
            for (let p = 2; p <= Math.min(lastPage, 10); p++) {
                extraPromises.push(
                    http
                        .get(`/cashflow?page=${p}&tgl_awal=${tglAwalCf}&tgl_akhir=${tglAkhirCf}${cabQuery}&is_proses=${statusFlag}`, {
                            headers: { Authorization: getAuthorizationHeader() },
                        })
                        .then(r => r?.data?.data?.data || [])
                        .catch(() => [])
                );
            }
            const extraPages = await Promise.all(extraPromises);
            return [...firstPageData, ...extraPages.flat()];
        };

        const [unprocessedList, processedList] = await Promise.all([
            fetchAllPagesByStatus(0),
            fetchAllPagesByStatus(1),
        ]);

        const allItems = [...unprocessedList, ...processedList].filter(item => {
            if (!targetCabangId) return true;
            if (!item.cabang_id && !item.user?.cabang_id) return true;
            const itemCab = String(item.cabang_id || item.user?.cabang_id || '');
            return itemCab === String(targetCabangId);
        });

        const grouped = { transfer: [], qris: [], edc: [] };
        allItems.forEach(item => {
            const key = getDigitalMethodKey(item.tipe);
            if (key) grouped[key].push(item);
        });

        const result = { transfer: null, qris: null, edc: null };
        ['transfer', 'qris', 'edc'].forEach(key => {
            const items = grouped[key];
            if (items.length > 0) {
                // Prioritaskan record yang belum diproses final (is_proses === 0) agar bisa di-update, lalu id terbaru
                const sorted = [...items].sort((a, b) => {
                    const aProc = Number(a.is_proses || 0);
                    const bProc = Number(b.is_proses || 0);
                    if (aProc !== bProc) return aProc - bProc;
                    return Number(b.id || 0) - Number(a.id || 0);
                });
                result[key] = {
                    record: sorted[0],
                    duplicates: sorted.slice(1),
                };
            }
        });

        return result;
    };

    const checkExistingCashflow = async (targetCabangId = cabangFilter) => {
        try {
            const map = await fetchExistingCashflowMap(targetCabangId);
            setExistingCashflow(map);
            return map;
        } catch (e) {
            return { transfer: null, qris: null, edc: null };
        }
    };

    const fetchData = async () => {
        setLoading(true);
        const tgl_awal = startDate ? moment(startDate).format('YYYY-MM-DD') : '';
        const tgl_akhir = endDate ? moment(endDate).format('YYYY-MM-DD') : '';

        let url = `/laporan-pembayaran?tgl_awal=${tgl_awal}&tgl_akhir=${tgl_akhir}`;
        if (cabangFilter) {
            url += `&cabang=${cabangFilter}`;
        }

        try {
            const res = await http.get(url, { headers: { Authorization: getAuthorizationHeader() } });
            const rawSummary = res.data.data || null;
            const detailList = Array.isArray(res.data.detail) ? res.data.detail : [];

            // Hitung rincian per bank untuk Transfer & EDC dari detail sebagai fallback/pelengkap
            const fallbackTransferBanks = {};
            const fallbackTransferCounts = {};
            const fallbackEdcBanks = {};
            const fallbackEdcCounts = {};
            const fallbackCounts = { cash: 0, qris: 0, transfer: 0, edc: 0 };

            detailList.forEach(p => {
                const m = (p.metode_pembayaran || '').toLowerCase();
                const nom = Number(p.nominal) || 0;
                const rawBank = (p.nama_bank || '').trim();
                const bankKey = rawBank ? rawBank.toUpperCase() : 'LAINNYA';

                if (m === 'cash') {
                    fallbackCounts.cash += 1;
                } else if (m === 'qris') {
                    fallbackCounts.qris += 1;
                } else if (m === 'transfer') {
                    fallbackCounts.transfer += 1;
                    fallbackTransferBanks[bankKey] = (fallbackTransferBanks[bankKey] || 0) + nom;
                    fallbackTransferCounts[bankKey] = (fallbackTransferCounts[bankKey] || 0) + 1;
                } else if (m === 'edc') {
                    fallbackCounts.edc += 1;
                    fallbackEdcBanks[bankKey] = (fallbackEdcBanks[bankKey] || 0) + nom;
                    fallbackEdcCounts[bankKey] = (fallbackEdcCounts[bankKey] || 0) + 1;
                }
            });

            if (rawSummary) {
                const transferBanks =
                    rawSummary.transfer?.banks && Object.keys(rawSummary.transfer.banks).length > 0
                        ? rawSummary.transfer.banks
                        : fallbackTransferBanks;
                const transferCounts =
                    rawSummary.transfer?.counts && Object.keys(rawSummary.transfer.counts).length > 0
                        ? rawSummary.transfer.counts
                        : fallbackTransferCounts;
                const edcBanks =
                    rawSummary.edc_banks && Object.keys(rawSummary.edc_banks).length > 0
                        ? rawSummary.edc_banks
                        : fallbackEdcBanks;
                const edcCounts =
                    rawSummary.edc_counts && Object.keys(rawSummary.edc_counts).length > 0
                        ? rawSummary.edc_counts
                        : fallbackEdcCounts;

                setSummary({
                    ...rawSummary,
                    transfer: {
                        total: Number(rawSummary.transfer?.total || 0),
                        banks: transferBanks,
                        counts: transferCounts,
                    },
                    edc: Number(rawSummary.edc || 0),
                    edc_banks: edcBanks,
                    edc_counts: edcCounts,
                    counts: rawSummary.counts || fallbackCounts,
                });
            } else {
                setSummary(null);
            }

            // Ambil split penjualan dari backend
            if (Array.isArray(res.data.split_penjualan)) {
                setSplitData(res.data.split_penjualan);
            } else if (detailList.length > 0) {
                // Fallback jika API mengembalikan data detail
                const grouped = {};
                detailList.forEach(p => {
                    const pId = p.penjualan_id || p.penjualan?.id;
                    if (!pId) return;
                    if (!grouped[pId]) {
                        grouped[pId] = {
                            id: pId,
                            kode_trx: p.penjualan?.kode_trx || `FKDJ-${pId}`,
                            created_at: p.penjualan?.created_at || p.created_at,
                            kasir: p.penjualan?.user?.name || '-',
                            cabang: p.penjualan?.user?.cabang?.nama_cabang || '-',
                            item: {
                                id: p.penjualan?.item?.id,
                                kode_item: p.penjualan?.item?.kode_item || '-',
                                nama_barang: p.penjualan?.item?.jenis_kategori?.nama_jenis || p.penjualan?.item?.nama_barang || '-',
                                karat: p.penjualan?.item?.karat || 0,
                                berat: p.penjualan?.item?.berat || 0,
                            },
                            harga_jual: Number(p.penjualan?.harga_jual) || 0,
                            total_harga: Number(p.penjualan?.total_harga || p.penjualan?.harga_jual) || 0,
                            payments: [],
                            cash: 0,
                            qris: 0,
                            transfer: 0,
                            transfer_bank: '',
                            edc: 0,
                            edc_bank: '',
                            jumlah_metode: 0
                        };
                    }
                    grouped[pId].payments.push(p);
                    const m = (p.metode_pembayaran || '').toLowerCase();
                    const nom = Number(p.nominal) || 0;
                    if (m === 'cash') grouped[pId].cash += nom;
                    else if (m === 'qris') grouped[pId].qris += nom;
                    else if (m === 'edc') {
                        grouped[pId].edc += nom;
                        if (p.nama_bank) {
                            grouped[pId].edc_bank = grouped[pId].edc_bank ? `${grouped[pId].edc_bank}, ${p.nama_bank}` : p.nama_bank;
                        }
                    } else if (m === 'transfer') {
                        grouped[pId].transfer += nom;
                        if (p.nama_bank) {
                            grouped[pId].transfer_bank = grouped[pId].transfer_bank ? `${grouped[pId].transfer_bank}, ${p.nama_bank}` : p.nama_bank;
                        }
                    }
                });

                const parsedSplits = Object.values(grouped).filter(item => item.payments.length > 1);
                parsedSplits.forEach(item => { item.jumlah_metode = item.payments.length; });
                setSplitData(parsedSplits);
            } else {
                setSplitData([]);
            }
        } catch (error) {
            toast.error("Gagal mengambil data pembayaran.");
        }
        setLoading(false);
    };

    // Sorted bank entries for Transfer & EDC
    const transferBankEntries = useMemo(() => {
        if (!summary?.transfer?.banks) return [];
        return Object.entries(summary.transfer.banks)
            .map(([bank, nominal]) => ({
                bank: bank || 'LAINNYA',
                nominal: Number(nominal) || 0,
                count: Number(summary.transfer?.counts?.[bank] || 0),
            }))
            .sort((a, b) => b.nominal - a.nominal);
    }, [summary]);

    const edcBankEntries = useMemo(() => {
        if (!summary?.edc_banks) return [];
        return Object.entries(summary.edc_banks)
            .map(([bank, nominal]) => ({
                bank: bank || 'LAINNYA',
                nominal: Number(nominal) || 0,
                count: Number(summary.edc_counts?.[bank] || 0),
            }))
            .sort((a, b) => b.nominal - a.nominal);
    }, [summary]);

    // Status per metode: apakah sudah ada di cashflow, apakah nominal sudah sama (sinkron), atau perlu diupdate
    const methodStatus = useMemo(() => {
        const getStatus = (key, currentNom) => {
            const nom = Number(currentNom || 0);
            const entry = existingCashflow[key];
            const record = entry?.record || null;
            const duplicates = entry?.duplicates || [];
            const isProcessed = Boolean(record);
            const existingNominal = Number(record?.nominal || 0);
            const hasDuplicates = duplicates.length > 0;
            const isClosedInKas = Number(record?.is_proses || 0) === 1;
            const isSynced = isProcessed && existingNominal === nom && !hasDuplicates;
            const needsUpdate = isProcessed && nom > 0 && (existingNominal !== nom || hasDuplicates);
            return {
                nom,
                record,
                duplicates,
                isProcessed,
                existingNominal,
                hasDuplicates,
                isClosedInKas,
                isSynced,
                needsUpdate,
            };
        };

        return {
            transfer: getStatus('transfer', summary?.transfer?.total),
            qris: getStatus('qris', summary?.qris),
            edc: getStatus('edc', summary?.edc),
        };
    }, [existingCashflow, summary]);

    const allDigitalSynced = useMemo(() => {
        const activeKeys = ['transfer', 'qris', 'edc'].filter(k => methodStatus[k].nom > 0);
        if (activeKeys.length === 0) return false;
        return activeKeys.every(k => methodStatus[k].isSynced);
    }, [methodStatus]);

    const anyDigitalNeedsUpdate = useMemo(() => {
        return ['transfer', 'qris', 'edc'].some(k => methodStatus[k].needsUpdate);
    }, [methodStatus]);

    // Buka Modal Proses Pembayaran Digital ke Pengeluaran Luar (Cashflow)
    // mode: 'all' | 'transfer' | 'qris' | 'edc'
    const openProsesModal = async (mode = 'all') => {
        if (!summary) {
            toast.warn('Data pembayaran belum tersedia.');
            return;
        }

        const totalTransfer = Number(summary.transfer?.total || 0);
        const totalQris = Number(summary.qris || 0);
        const totalEdc = Number(summary.edc || 0);

        if (mode === 'all' && totalTransfer <= 0 && totalQris <= 0 && totalEdc <= 0) {
            toast.warn('Tidak ada nominal pembayaran Transfer, QRIS, maupun EDC untuk diproses.');
            return;
        }
        if (mode === 'transfer' && totalTransfer <= 0) {
            toast.warn('Tidak ada nominal pembayaran Transfer untuk diproses.');
            return;
        }
        if (mode === 'qris' && totalQris <= 0) {
            toast.warn('Tidak ada nominal pembayaran QRIS untuk diproses.');
            return;
        }
        if (mode === 'edc' && totalEdc <= 0) {
            toast.warn('Tidak ada nominal pembayaran EDC untuk diproses.');
            return;
        }

        // Jika klik tombol spesifik tapi sudah diproses dan nominal sama persis, cegah proses berulang
        if (mode !== 'all' && methodStatus[mode]?.isSynced) {
            toast.info(`Metode ${mode.toUpperCase()} sudah diproses dengan nominal yang sama di Pengeluaran Luar.`);
            return;
        }

        const defaultCabang = cabangFilter || (cabangList[0]?.id ? String(cabangList[0].id) : '');
        setProsesCabangId(defaultCabang);

        // Pastikan status cashflow untuk cabang terpilih paling mutakhir
        const freshMap = await checkExistingCashflow(defaultCabang);
        const isMethodSyncedInMap = (key, nom) => {
            const rec = freshMap[key]?.record;
            const dups = freshMap[key]?.duplicates || [];
            return Boolean(rec) && Number(rec.nominal || 0) === nom && dups.length === 0;
        };

        const periodeLabel = startDate && endDate
            ? (awal === akhir ? awal : `${awal} s/d ${akhir}`)
            : moment().format('DD-MM-YYYY');

        const transferDetailText = transferBankEntries.length > 0
            ? `Transfer Bank (${transferBankEntries.map(b => `${b.bank}: Rp ${numberWithCommas(b.nominal)}`).join(', ')}) - ${periodeLabel}`
            : `Pembayaran Transfer Bank - ${periodeLabel}`;

        const qrisDetailText = `Pembayaran QRIS (${summary.counts?.qris || 0} Trx) - ${periodeLabel}`;

        const edcDetailText = edcBankEntries.length > 0
            ? `Pembayaran EDC (${edcBankEntries.map(b => `${b.bank}: Rp ${numberWithCommas(b.nominal)}`).join(', ')}) - ${periodeLabel}`
            : `Pembayaran EDC - ${periodeLabel}`;

        setProsesKeterangan({
            transfer: freshMap.transfer?.record?.keterangan && isMethodSyncedInMap('transfer', totalTransfer)
                ? freshMap.transfer.record.keterangan
                : transferDetailText,
            qris: freshMap.qris?.record?.keterangan && isMethodSyncedInMap('qris', totalQris)
                ? freshMap.qris.record.keterangan
                : qrisDetailText,
            edc: freshMap.edc?.record?.keterangan && isMethodSyncedInMap('edc', totalEdc)
                ? freshMap.edc.record.keterangan
                : edcDetailText,
        });

        setProsesSelection({
            transfer:
                mode === 'all'
                    ? totalTransfer > 0 && !isMethodSyncedInMap('transfer', totalTransfer)
                    : mode === 'transfer' && !isMethodSyncedInMap('transfer', totalTransfer),
            qris:
                mode === 'all'
                    ? totalQris > 0 && !isMethodSyncedInMap('qris', totalQris)
                    : mode === 'qris' && !isMethodSyncedInMap('qris', totalQris),
            edc:
                mode === 'all'
                    ? totalEdc > 0 && !isMethodSyncedInMap('edc', totalEdc)
                    : mode === 'edc' && !isMethodSyncedInMap('edc', totalEdc),
        });

        setIsOpenProsesModal(true);
    };

    const handleChangeProsesCabang = async (newCabangId) => {
        setProsesCabangId(newCabangId);
        if (!summary) return;
        const totalTransfer = Number(summary.transfer?.total || 0);
        const totalQris = Number(summary.qris || 0);
        const totalEdc = Number(summary.edc || 0);
        const freshMap = await checkExistingCashflow(newCabangId);
        const isMethodSyncedInMap = (key, nom) => {
            const rec = freshMap[key]?.record;
            const dups = freshMap[key]?.duplicates || [];
            return Boolean(rec) && Number(rec.nominal || 0) === nom && dups.length === 0;
        };
        setProsesSelection({
            transfer: totalTransfer > 0 && !isMethodSyncedInMap('transfer', totalTransfer),
            qris: totalQris > 0 && !isMethodSyncedInMap('qris', totalQris),
            edc: totalEdc > 0 && !isMethodSyncedInMap('edc', totalEdc),
        });
    };

    const handleSubmitProsesCashflow = async (e) => {
        e.preventDefault();
        if (!summary) return;

        if ((role === 'admin' || role === 'owner') && !prosesCabangId && cabangList.length > 0) {
            toast.warning('Pilih cabang/toko tujuan terlebih dahulu.');
            return;
        }

        const itemsToSubmit = [];
        const totalTransfer = Number(summary.transfer?.total || 0);
        const totalQris = Number(summary.qris || 0);
        const totalEdc = Number(summary.edc || 0);

        if (prosesSelection.transfer && totalTransfer > 0) {
            itemsToSubmit.push({
                key: 'transfer',
                title: 'Digital Pay Transfer',
                tipe: 'digitalpay-transfer',
                nominal: totalTransfer,
                keterangan: prosesKeterangan.transfer || 'Digital Pay Transfer',
                cabang_id: prosesCabangId || undefined,
            });
        }

        if (prosesSelection.qris && totalQris > 0) {
            itemsToSubmit.push({
                key: 'qris',
                title: 'Digital Pay QRIS',
                tipe: 'digitalpay-qris',
                nominal: totalQris,
                keterangan: prosesKeterangan.qris || 'Digital Pay QRIS',
                cabang_id: prosesCabangId || undefined,
            });
        }

        if (prosesSelection.edc && totalEdc > 0) {
            itemsToSubmit.push({
                key: 'edc',
                title: 'Digital Pay EDC',
                tipe: 'digitalpay-edc-debit-kredit',
                nominal: totalEdc,
                keterangan: prosesKeterangan.edc || 'Digital Pay EDC',
                cabang_id: prosesCabangId || undefined,
            });
        }

        if (itemsToSubmit.length === 0) {
            toast.warning('Pilih minimal satu metode pembayaran yang belum sinkron atau perlu diupdate.');
            return;
        }

        setLoadingProses(true);
        try {
            // Ambil data cashflow terbaru untuk cabang terkait guna memastikan upsert akurat (tidak pernah duplikat record)
            const targetCabang = prosesCabangId || cabangFilter;
            const latestMap = await fetchExistingCashflowMap(targetCabang);

            const createdLabels = [];
            const updatedLabels = [];

            for (const item of itemsToSubmit) {
                const existingEntry = latestMap[item.key];
                const existingRecord = existingEntry?.record;
                const duplicateRecords = existingEntry?.duplicates || [];

                const payload = {
                    title: item.title,
                    tipe: item.tipe,
                    nominal: item.nominal,
                    keterangan: item.keterangan,
                    cabang_id: item.cabang_id,
                };

                if (existingRecord?.id) {
                    // UPDATE record yang sudah ada (TIDAK membuat record baru di Pengeluaran Luar)
                    await http.put(`/cashflow/${existingRecord.id}`, payload, {
                        headers: { Authorization: getAuthorizationHeader() },
                    });
                    updatedLabels.push(item.title);

                    // Bersihkan jika sebelumnya sempat ada duplikat record yang belum diproses final (is_proses === 0)
                    for (const dup of duplicateRecords) {
                        if (dup?.id && Number(dup.is_proses || 0) === 0) {
                            await http
                                .delete(`/cashflow/${dup.id}`, {
                                    headers: { Authorization: getAuthorizationHeader() },
                                })
                                .catch(() => {});
                        }
                    }
                } else {
                    // BUAT record baru hanya jika memang belum pernah ada di Pengeluaran Luar pada periode/cabang ini
                    await http.post('/cashflow', payload, {
                        headers: { Authorization: getAuthorizationHeader() },
                    });
                    createdLabels.push(item.title);
                }
            }

            if (createdLabels.length > 0 && updatedLabels.length > 0) {
                toast.success(
                    `Berhasil memproses (${createdLabels.join(', ')}) & memperbarui (${updatedLabels.join(', ')}) di Pengeluaran Luar!`
                );
            } else if (updatedLabels.length > 0) {
                toast.success(
                    `Berhasil memperbarui nominal ${updatedLabels.join(', ')} pada record Pengeluaran Luar (tanpa membuat baris baru)!`
                );
            } else {
                toast.success(`Berhasil memproses ${createdLabels.join(', ')} ke Pengeluaran Luar!`);
            }

            setIsOpenProsesModal(false);
            await checkExistingCashflow(cabangFilter);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Gagal memproses data ke Pengeluaran Luar.');
        }
        setLoadingProses(false);
    };

    // Filtered Split Data
    const filteredSplitData = useMemo(() => {
        return splitData.filter(trx => {
            if (searchKeyword.trim() !== '') {
                const q = searchKeyword.toLowerCase();
                const kodeTrxMatch = trx.kode_trx?.toLowerCase().includes(q);
                const kodeItemMatch = trx.item?.kode_item?.toLowerCase().includes(q);
                const namaBarangMatch = trx.item?.nama_barang?.toLowerCase().includes(q);
                const kasirMatch = trx.kasir?.toLowerCase().includes(q);
                const cabangMatch = trx.cabang?.toLowerCase().includes(q);

                if (!kodeTrxMatch && !kodeItemMatch && !namaBarangMatch && !kasirMatch && !cabangMatch) {
                    return false;
                }
            }

            if (metodeFilter === 'cash' && (trx.cash || 0) <= 0) return false;
            if (metodeFilter === 'qris' && (trx.qris || 0) <= 0) return false;
            if (metodeFilter === 'transfer' && (trx.transfer || 0) <= 0) return false;
            if (metodeFilter === 'edc' && (trx.edc || 0) <= 0) return false;

            return true;
        });
    }, [splitData, searchKeyword, metodeFilter]);

    // Totals for Split Data
    const splitStats = useMemo(() => {
        let totalNilai = 0;
        let totalCash = 0;
        let totalQris = 0;
        let totalTransfer = 0;
        let totalEdc = 0;

        filteredSplitData.forEach(trx => {
            totalNilai += Number(trx.total_harga) || Number(trx.harga_jual) || 0;
            totalCash += Number(trx.cash) || 0;
            totalQris += Number(trx.qris) || 0;
            totalTransfer += Number(trx.transfer) || 0;
            totalEdc += Number(trx.edc) || 0;
        });

        return {
            count: filteredSplitData.length,
            totalNilai,
            totalCash,
            totalQris,
            totalTransfer,
            totalEdc
        };
    }, [filteredSplitData]);

    // Pagination
    const totalPages = Math.ceil(filteredSplitData.length / pageSize) || 1;
    const paginatedSplitData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredSplitData.slice(start, start + pageSize);
    }, [filteredSplitData, currentPage, pageSize]);

    const getSharePercentage = (nominal, total) => {
        const num = Number(nominal) || 0;
        const den = Number(total) || 0;
        if (den <= 0 || num <= 0) return 0;
        return Math.min(100, Number(((num / den) * 100).toFixed(1)));
    };

    const totalGrand = Number(summary?.total_keseluruhan || 0);
    const cashShare = getSharePercentage(summary?.cash, totalGrand);
    const qrisShare = getSharePercentage(summary?.qris, totalGrand);
    const transferShare = getSharePercentage(summary?.transfer?.total, totalGrand);
    const edcShare = getSharePercentage(summary?.edc, totalGrand);

    const totalDigitalPaySelected = useMemo(() => {
        if (!summary) return 0;
        let sum = 0;
        if (prosesSelection.transfer && !methodStatus.transfer.isSynced) sum += Number(summary.transfer?.total || 0);
        if (prosesSelection.qris && !methodStatus.qris.isSynced) sum += Number(summary.qris || 0);
        if (prosesSelection.edc && !methodStatus.edc.isSynced) sum += Number(summary.edc || 0);
        return sum;
    }, [summary, prosesSelection, methodStatus]);

    const hasAnyDigitalNominal = useMemo(() => {
        if (!summary) return false;
        return (
            Number(summary.transfer?.total || 0) > 0 ||
            Number(summary.qris || 0) > 0 ||
            Number(summary.edc || 0) > 0
        );
    }, [summary]);

    return (
        <Layouts title={'Laporan Metode Pembayaran'}>
            <div className="bg-slate-50 dark:bg-slate-900 min-h-screen pb-12">
                {/* Header Section */}
                <div className="bg-white dark:bg-slate-800 px-5 pt-5 pb-0 md:px-6 md:pt-6 border-b border-slate-200/80 dark:border-slate-700 shadow-xs">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-end gap-4">
                        {/* Global Filters & Proses Button */}
                        <div className="flex flex-wrap items-center justify-end gap-2.5 w-full">
                            {(role === 'admin' || role === 'owner') && (
                                <select
                                    className="select select-bordered select-sm bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-white rounded-xl text-xs font-semibold h-[38px] shadow-2xs focus:border-slate-900 dark:focus:border-slate-100 focus:outline-none"
                                    value={cabangFilter}
                                    onChange={(e) => {
                                        setCabangFilter(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="">Semua Cabang</option>
                                    {cabangList.map(cab => (
                                        <option key={cab.id} value={cab.id}>{cab.nama_cabang}</option>
                                    ))}
                                </select>
                            )}

                            {/* Date Range Dropdown */}
                            <div className="dropdown dropdown-bottom dropdown-end">
                                <div
                                    tabIndex={0}
                                    className="px-3.5 py-2 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 cursor-pointer hover:border-slate-400 hover:bg-slate-50/60 dark:hover:bg-slate-700/60 transition-all duration-200 flex items-center gap-2 text-xs h-[38px] shadow-2xs"
                                >
                                    <IconCalendarTime size={16} className="text-slate-700 dark:text-slate-300 shrink-0" />
                                    <span className="font-semibold">
                                        {startDate && endDate ? (awal === akhir ? awal : `${awal} - ${akhir}`) : 'Semua Tanggal'}
                                    </span>
                                </div>
                                <div tabIndex={0} className="dropdown-content menu p-2 shadow-2xl bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 z-50 mt-1.5 right-0 w-max">
                                    <DateRange ranges={[selectionRange]} onChange={handleSelectDate} />
                                    <div className="p-2 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between gap-2">
                                        <button
                                            type="button"
                                            onClick={handleRefreshToday}
                                            className="text-xs text-slate-800 dark:text-slate-200 hover:underline font-semibold"
                                        >
                                            Hari Ini
                                        </button>
                                        {startDate && (
                                            <button
                                                type="button"
                                                onClick={handleResetDate}
                                                className="text-xs text-slate-500 hover:underline font-semibold"
                                            >
                                                Reset Tanggal (Semua)
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Refresh Icon-Only Button with White Background (resets to today's default date) */}
                            <button
                                type="button"
                                onClick={handleRefreshToday}
                                className="w-[38px] h-[38px] inline-flex items-center justify-center bg-white hover:bg-slate-50 active:scale-95 text-slate-700 hover:text-black border border-slate-200/90 hover:border-slate-400 rounded-xl shadow-2xs transition-all duration-200 disabled:opacity-50"
                                disabled={loading}
                                title="Reset ke Tanggal Hari Ini & Muat Ulang"
                                aria-label="Refresh ke Tanggal Hari Ini"
                            >
                                <IconReload size={17} className={loading ? 'animate-spin text-slate-800' : 'text-slate-700'} />
                            </button>

                            {/* Tombol Proses ke Pengeluaran Luar (Cashflow) */}
                            <button
                                type="button"
                                onClick={() => openProsesModal('all')}
                                disabled={loading || !hasAnyDigitalNominal || (allDigitalSynced && Boolean(cabangFilter || (role !== 'admin' && role !== 'owner')))}
                                className={`h-[38px] px-4 inline-flex items-center gap-2 font-bold text-xs rounded-xl shadow-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
                                    allDigitalSynced && Boolean(cabangFilter || (role !== 'admin' && role !== 'owner'))
                                        ? 'bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
                                        : 'bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white shadow-amber-500/20'
                                }`}
                                title={
                                    allDigitalSynced
                                        ? 'Seluruh metode pembayaran digital sudah diproses & sinkron di Pengeluaran Luar'
                                        : 'Proses Pembayaran Transfer, QRIS, dan EDC ke Cashflow Pengeluaran Luar'
                                }
                            >
                                {allDigitalSynced && Boolean(cabangFilter || (role !== 'admin' && role !== 'owner')) ? (
                                    <>
                                        <IconCheck size={16} />
                                        <span>Sudah Diproses</span>
                                    </>
                                ) : (
                                    <>
                                        <IconArrowsRightLeft size={16} />
                                        <span>{anyDigitalNeedsUpdate ? 'Update Proses' : 'Proses'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="flex items-center gap-2 mt-5 pt-1">
                        <button
                            type="button"
                            onClick={() => setActiveTab('rekap')}
                            className={`flex items-center gap-2 pb-3 px-4 font-bold text-xs md:text-sm transition-all border-b-2 relative ${
                                activeTab === 'rekap'
                                    ? 'border-slate-900 text-slate-900 dark:border-white dark:text-white'
                                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                            }`}
                        >
                            <IconChartPie size={16} />
                            <span>Rekapitulasi & Rincian Bank</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => setActiveTab('split')}
                            className={`flex items-center gap-2 pb-3 px-4 font-bold text-xs md:text-sm transition-all border-b-2 relative ${
                                activeTab === 'split'
                                    ? 'border-slate-900 text-slate-900 dark:border-white dark:text-white'
                                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                            }`}
                        >
                            <IconArrowsSplit size={16} />
                            <span>Transaksi Split Bayar</span>
                        </button>
                    </div>
                </div>

                {/* Content Body */}
                <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
                    {loading ? (
                        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-700 p-20 flex flex-col items-center justify-center gap-3 shadow-xs">
                            <div className="w-11 h-11 rounded-full animate-spin border-4 border-slate-200 border-t-slate-900 dark:border-slate-700 dark:border-t-white" />
                            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                                Memuat data laporan pembayaran...
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* TAB 1: REKAPITULASI METODE PEMBAYARAN */}
                            {activeTab === 'rekap' && (
                                summary ? (
                                    <div className="space-y-6">
                                        {/* Top Summary Cards */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                                            {/* 1. Cash Card */}
                                            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow p-[17px] rounded-lg flex flex-col justify-between">
                                                <div className="flex flex-row items-center">
                                                    <div className="flex-shrink pr-4">
                                                        <div className="p-3 bg-emerald-500 rounded-lg text-white shadow-sm shadow-emerald-500/20">
                                                            <IconCash size={24} className="text-white" />
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 text-right min-w-0">
                                                        <h5 className="font-bold uppercase text-gray-500 dark:text-slate-400 text-xs truncate">
                                                            Total Cash / Tunai
                                                        </h5>
                                                        <h3 className="font-bold text-base xl:text-lg text-slate-800 dark:text-slate-100 truncate mt-0.5">
                                                            Rp {numberWithCommas(summary.cash)}
                                                        </h3>
                                                    </div>
                                                </div>
                                                <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
                                                    <span className="text-[11px] font-medium text-gray-500 dark:text-slate-400">Masuk Kas Tunai</span>
                                                    <span className="text-[11px] font-bold text-slate-800 dark:text-slate-200">
                                                        {summary.counts?.cash !== undefined ? `${summary.counts.cash} Trx` : ''} ({cashShare}%)
                                                    </span>
                                                </div>
                                            </div>

                                            {/* 2. QRIS Card */}
                                            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow p-[17px] rounded-lg flex flex-col justify-between">
                                                <div className="flex flex-row items-center">
                                                    <div className="flex-shrink pr-4">
                                                        <div className="p-3 bg-orange-500 rounded-lg text-white shadow-sm shadow-orange-500/20">
                                                            <IconQrcode size={24} className="text-white" />
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 text-right min-w-0">
                                                        <h5 className="font-bold uppercase text-gray-500 dark:text-slate-400 text-xs truncate">
                                                            Total QRIS
                                                        </h5>
                                                        <h3 className="font-bold text-base xl:text-lg text-slate-800 dark:text-slate-100 truncate mt-0.5">
                                                            Rp {numberWithCommas(summary.qris)}
                                                        </h3>
                                                    </div>
                                                </div>
                                                <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2 text-xs">
                                                    <div className="flex items-center gap-1.5 min-w-0">
                                                        <span className="text-[11px] font-bold text-slate-800 dark:text-slate-200 truncate">
                                                            {summary.counts?.qris !== undefined ? `${summary.counts.qris} Trx` : 'Digital Pay'} ({qrisShare}%)
                                                        </span>
                                                        {methodStatus.qris.isSynced ? (
                                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800">
                                                                <IconCheck size={10} /> Diproses
                                                            </span>
                                                        ) : methodStatus.qris.needsUpdate ? (
                                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800">
                                                                Update
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => openProsesModal('qris')}
                                                        disabled={Number(summary.qris || 0) <= 0 || methodStatus.qris.isSynced}
                                                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold border transition shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                                                            methodStatus.qris.isSynced
                                                                ? 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600'
                                                                : 'bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white border-transparent shadow-xs shadow-amber-500/20'
                                                        }`}
                                                    >
                                                        {methodStatus.qris.isSynced ? (
                                                            <>
                                                                <IconCheck size={12} />
                                                                <span>Sudah Diproses</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <IconArrowsRightLeft size={12} />
                                                                <span>{methodStatus.qris.needsUpdate ? 'Update QRIS' : 'Proses QRIS'}</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* 3. Transfer Bank Card */}
                                            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow p-[17px] rounded-lg flex flex-col justify-between">
                                                <div className="flex flex-row items-center">
                                                    <div className="flex-shrink pr-4">
                                                        <div className="p-3 bg-blue-600 rounded-lg text-white shadow-sm shadow-blue-600/20">
                                                            <IconBuildingBank size={24} className="text-white" />
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 text-right min-w-0">
                                                        <h5 className="font-bold uppercase text-gray-500 dark:text-slate-400 text-xs truncate">
                                                            Total Transfer Bank
                                                        </h5>
                                                        <h3 className="font-bold text-base xl:text-lg text-slate-800 dark:text-slate-100 truncate mt-0.5">
                                                            Rp {numberWithCommas(summary.transfer?.total || 0)}
                                                        </h3>
                                                    </div>
                                                </div>
                                                <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2 text-xs">
                                                    <div className="flex items-center gap-1.5 min-w-0">
                                                        <span className="text-[11px] font-bold text-slate-800 dark:text-slate-200 truncate">
                                                            {summary.counts?.transfer !== undefined ? `${summary.counts.transfer} Trx` : `${transferBankEntries.length} Bank`} ({transferShare}%)
                                                        </span>
                                                        {methodStatus.transfer.isSynced ? (
                                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800">
                                                                <IconCheck size={10} /> Diproses
                                                            </span>
                                                        ) : methodStatus.transfer.needsUpdate ? (
                                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800">
                                                                Update
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => openProsesModal('transfer')}
                                                        disabled={Number(summary.transfer?.total || 0) <= 0 || methodStatus.transfer.isSynced}
                                                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold border transition shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                                                            methodStatus.transfer.isSynced
                                                                ? 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600'
                                                                : 'bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white border-transparent shadow-xs shadow-amber-500/20'
                                                        }`}
                                                    >
                                                        {methodStatus.transfer.isSynced ? (
                                                            <>
                                                                <IconCheck size={12} />
                                                                <span>Sudah Diproses</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <IconArrowsRightLeft size={12} />
                                                                <span>{methodStatus.transfer.needsUpdate ? 'Update Transfer' : 'Proses Transfer'}</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* 4. EDC / Debit Card */}
                                            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow p-[17px] rounded-lg flex flex-col justify-between">
                                                <div className="flex flex-row items-center">
                                                    <div className="flex-shrink pr-4">
                                                        <div className="p-3 bg-purple-600 rounded-lg text-white shadow-sm shadow-purple-600/20">
                                                            <IconCreditCard size={24} className="text-white" />
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 text-right min-w-0">
                                                        <h5 className="font-bold uppercase text-gray-500 dark:text-slate-400 text-xs truncate">
                                                            Total EDC / Debit
                                                        </h5>
                                                        <h3 className="font-bold text-base xl:text-lg text-slate-800 dark:text-slate-100 truncate mt-0.5">
                                                            Rp {numberWithCommas(summary.edc || 0)}
                                                        </h3>
                                                    </div>
                                                </div>
                                                <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2 text-xs">
                                                    <div className="flex items-center gap-1.5 min-w-0">
                                                        <span className="text-[11px] font-bold text-slate-800 dark:text-slate-200 truncate">
                                                            {summary.counts?.edc !== undefined ? `${summary.counts.edc} Trx` : `${edcBankEntries.length} Bank`} ({edcShare}%)
                                                        </span>
                                                        {methodStatus.edc.isSynced ? (
                                                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800">
                                                                <IconCheck size={10} /> Diproses
                                                            </span>
                                                        ) : methodStatus.edc.needsUpdate ? (
                                                            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800">
                                                                Update
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => openProsesModal('edc')}
                                                        disabled={Number(summary.edc || 0) <= 0 || methodStatus.edc.isSynced}
                                                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold border transition shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                                                            methodStatus.edc.isSynced
                                                                ? 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600'
                                                                : 'bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white border-transparent shadow-xs shadow-amber-500/20'
                                                        }`}
                                                    >
                                                        {methodStatus.edc.isSynced ? (
                                                            <>
                                                                <IconCheck size={12} />
                                                                <span>Sudah Diproses</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <IconArrowsRightLeft size={12} />
                                                                <span>{methodStatus.edc.needsUpdate ? 'Update EDC' : 'Proses EDC'}</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* 5. Total Keseluruhan Card */}
                                            <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow p-[17px] rounded-lg flex flex-col justify-between">
                                                <div className="flex flex-row items-center">
                                                    <div className="flex-shrink pr-4">
                                                        <div className="p-3 bg-amber-500 rounded-lg text-white shadow-sm shadow-amber-500/20">
                                                            <IconReceipt2 size={24} className="text-white" />
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 text-right min-w-0">
                                                        <h5 className="font-bold uppercase text-gray-500 dark:text-slate-400 text-xs truncate">
                                                            Total Keseluruhan
                                                        </h5>
                                                        <h3 className="font-bold text-base xl:text-lg text-slate-800 dark:text-slate-100 truncate mt-0.5">
                                                            Rp {numberWithCommas(summary.total_keseluruhan)}
                                                        </h3>
                                                    </div>
                                                </div>
                                                <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
                                                    <span className="text-[11px] font-medium text-gray-500 dark:text-slate-400">Akumulasi Semua Metode</span>
                                                    <span className="text-[11px] font-bold text-slate-800 dark:text-slate-200">100%</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Separated Rincian Per Bank: Transfer Bank vs EDC (Side-by-Side) */}
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {/* CARD 1: RINCIAN TRANSFER BANK SETIAP BANK */}
                                            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-xs overflow-hidden flex flex-col">
                                                <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700/80 bg-slate-50 dark:bg-slate-800/80 flex items-center justify-between gap-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                                                            <IconBuildingBank size={20} />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-extrabold text-slate-900 dark:text-white text-sm md:text-base">
                                                                Rincian Transfer Bank
                                                            </h3>
                                                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                                                Akumulasi penerimaan transfer pada setiap bank
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-900 border border-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600 shrink-0">
                                                        {transferBankEntries.length} Bank
                                                    </span>
                                                </div>

                                                <div className="p-5 flex-1 flex flex-col justify-between">
                                                    {transferBankEntries.length > 0 ? (
                                                        <div className="space-y-3">
                                                            {transferBankEntries.map((item, index) => {
                                                                const pct = getSharePercentage(item.nominal, summary.transfer?.total || 0);
                                                                return (
                                                                    <div
                                                                        key={item.bank}
                                                                        className="p-3.5 rounded-xl border border-slate-200/70 dark:border-slate-700/70 bg-slate-50/50 dark:bg-slate-800/50 hover:bg-slate-100/70 dark:hover:bg-slate-700/50 hover:border-slate-300 dark:hover:border-slate-600 transition-all"
                                                                    >
                                                                        <div className="flex items-center justify-between gap-3">
                                                                            <div className="flex items-center gap-3 min-w-0">
                                                                                <span className="w-6 h-6 rounded-lg bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-[11px] font-bold text-slate-700 dark:text-slate-300 flex items-center justify-center shrink-0">
                                                                                    {index + 1}
                                                                                </span>
                                                                                <div className="min-w-0">
                                                                                    <div className="flex items-center gap-2">
                                                                                        <span className="font-bold text-slate-900 dark:text-slate-100 text-xs sm:text-sm truncate">
                                                                                            Bank {item.bank}
                                                                                        </span>
                                                                                        {item.count > 0 && (
                                                                                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200">
                                                                                                {item.count} Trx
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            <div className="text-right shrink-0">
                                                                                <p className="font-extrabold text-slate-900 dark:text-white text-xs sm:text-sm">
                                                                                    Rp {numberWithCommas(item.nominal)}
                                                                                </p>
                                                                                <p className="text-[10px] font-semibold text-slate-400">
                                                                                    {pct}% dari total transfer
                                                                                </p>
                                                                            </div>
                                                                        </div>

                                                                        {/* Progress bar */}
                                                                        <div className="mt-2.5 w-full h-1.5 bg-slate-200/70 dark:bg-slate-700 rounded-full overflow-hidden">
                                                                            <div
                                                                                className="h-full bg-blue-600 rounded-full transition-all duration-500"
                                                                                style={{ width: `${Math.max(pct, 3)}%` }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div className="py-12 px-4 text-center flex flex-col items-center justify-center gap-2">
                                                            <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-700/50 text-slate-400 flex items-center justify-center">
                                                                <IconBuildingBank size={24} />
                                                            </div>
                                                            <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">
                                                                Belum ada transaksi Transfer Bank
                                                            </p>
                                                            <p className="text-xs text-slate-400">
                                                                Tidak ditemukan pembayaran via transfer bank pada periode ini.
                                                            </p>
                                                        </div>
                                                    )}

                                                    {/* Footer Total Transfer */}
                                                    <div className="mt-5 pt-4 border-t border-slate-200/80 dark:border-slate-700 flex items-center justify-between">
                                                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                                            Total Transfer Keseluruhan
                                                        </span>
                                                        <span className="text-base sm:text-lg font-black text-slate-900 dark:text-white">
                                                            Rp {numberWithCommas(summary.transfer?.total || 0)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* CARD 2: RINCIAN EDC / DEBIT SETIAP BANK */}
                                            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-xs overflow-hidden flex flex-col">
                                                <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700/80 bg-slate-50 dark:bg-slate-800/80 flex items-center justify-between gap-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
                                                            <IconCreditCard size={20} />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-extrabold text-slate-900 dark:text-white text-sm md:text-base">
                                                                Rincian EDC / Debit
                                                            </h3>
                                                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                                                Akumulasi penerimaan mesin EDC pada setiap bank
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-900 border border-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600 shrink-0">
                                                        {edcBankEntries.length} Bank
                                                    </span>
                                                </div>

                                                <div className="p-5 flex-1 flex flex-col justify-between">
                                                    {edcBankEntries.length > 0 ? (
                                                        <div className="space-y-3">
                                                            {edcBankEntries.map((item, index) => {
                                                                const pct = getSharePercentage(item.nominal, summary.edc || 0);
                                                                return (
                                                                    <div
                                                                        key={item.bank}
                                                                        className="p-3.5 rounded-xl border border-slate-200/70 dark:border-slate-700/70 bg-slate-50/50 dark:bg-slate-800/50 hover:bg-slate-100/70 dark:hover:bg-slate-700/50 hover:border-slate-300 dark:hover:border-slate-600 transition-all"
                                                                    >
                                                                        <div className="flex items-center justify-between gap-3">
                                                                            <div className="flex items-center gap-3 min-w-0">
                                                                                <span className="w-6 h-6 rounded-lg bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-[11px] font-bold text-slate-700 dark:text-slate-300 flex items-center justify-center shrink-0">
                                                                                    {index + 1}
                                                                                </span>
                                                                                <div className="min-w-0">
                                                                                    <div className="flex items-center gap-2">
                                                                                        <span className="font-bold text-slate-900 dark:text-slate-100 text-xs sm:text-sm truncate">
                                                                                            Bank {item.bank}
                                                                                        </span>
                                                                                        {item.count > 0 && (
                                                                                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200">
                                                                                                {item.count} Trx
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            <div className="text-right shrink-0">
                                                                                <p className="font-extrabold text-slate-900 dark:text-white text-xs sm:text-sm">
                                                                                    Rp {numberWithCommas(item.nominal)}
                                                                                </p>
                                                                                <p className="text-[10px] font-semibold text-slate-400">
                                                                                    {pct}% dari total EDC
                                                                                </p>
                                                                            </div>
                                                                        </div>

                                                                        {/* Progress bar */}
                                                                        <div className="mt-2.5 w-full h-1.5 bg-slate-200/70 dark:bg-slate-700 rounded-full overflow-hidden">
                                                                            <div
                                                                                className="h-full bg-purple-600 rounded-full transition-all duration-500"
                                                                                style={{ width: `${Math.max(pct, 3)}%` }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div className="py-12 px-4 text-center flex flex-col items-center justify-center gap-2">
                                                            <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-700/50 text-slate-400 flex items-center justify-center">
                                                                <IconCreditCard size={24} />
                                                            </div>
                                                            <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">
                                                                Belum ada transaksi EDC / Debit
                                                            </p>
                                                            <p className="text-xs text-slate-400">
                                                                Tidak ditemukan pembayaran via mesin EDC pada periode ini.
                                                            </p>
                                                        </div>
                                                    )}

                                                    {/* Footer Total EDC */}
                                                    <div className="mt-5 pt-4 border-t border-slate-200/80 dark:border-slate-700 flex items-center justify-between">
                                                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                                            Total EDC Keseluruhan
                                                        </span>
                                                        <span className="text-base sm:text-lg font-black text-slate-900 dark:text-white">
                                                            Rp {numberWithCommas(summary.edc || 0)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-700 p-16 text-center text-slate-500">
                                        Pilih rentang tanggal dan cabang untuk menampilkan rekapitulasi.
                                    </div>
                                )
                            )}

                            {/* TAB 2: TRANSAKSI SPLIT PEMBAYARAN */}
                            {activeTab === 'split' && (
                                <div className="space-y-5">
                                    {/* Mini Stats for Split Payments */}
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
                                        {/* Total Transaksi Split */}
                                        <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow p-[17px] rounded-lg">
                                            <div className="flex flex-row items-center">
                                                <div className="flex-shrink pr-3">
                                                    <div className="p-2.5 bg-indigo-500 rounded-lg text-white shadow-sm shadow-indigo-500/20">
                                                        <IconArrowsSplit size={20} className="text-white" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 text-right min-w-0">
                                                    <h5 className="font-bold uppercase text-gray-500 dark:text-slate-400 text-xs truncate">Trx Split</h5>
                                                    <h3 className="font-bold text-base text-slate-800 dark:text-slate-100 truncate mt-0.5">
                                                        {splitStats.count} <span className="text-xs font-normal text-slate-400">Trx</span>
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Total Nilai Split */}
                                        <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow p-[17px] rounded-lg">
                                            <div className="flex flex-row items-center">
                                                <div className="flex-shrink pr-3">
                                                    <div className="p-2.5 bg-amber-500 rounded-lg text-white shadow-sm shadow-amber-500/20">
                                                        <IconReceipt2 size={20} className="text-white" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 text-right min-w-0">
                                                    <h5 className="font-bold uppercase text-gray-500 dark:text-slate-400 text-xs truncate">Total Penjualan</h5>
                                                    <h3 className="font-bold text-base text-slate-800 dark:text-slate-100 truncate mt-0.5">
                                                        Rp {numberWithCommas(splitStats.totalNilai)}
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Split Cash */}
                                        <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow p-[17px] rounded-lg">
                                            <div className="flex flex-row items-center">
                                                <div className="flex-shrink pr-3">
                                                    <div className="p-2.5 bg-emerald-500 rounded-lg text-white shadow-sm shadow-emerald-500/20">
                                                        <IconCash size={20} className="text-white" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 text-right min-w-0">
                                                    <h5 className="font-bold uppercase text-gray-500 dark:text-slate-400 text-xs truncate">Split Cash</h5>
                                                    <h3 className="font-bold text-base text-slate-800 dark:text-slate-100 truncate mt-0.5">
                                                        Rp {numberWithCommas(splitStats.totalCash)}
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Split QRIS */}
                                        <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow p-[17px] rounded-lg">
                                            <div className="flex flex-row items-center">
                                                <div className="flex-shrink pr-3">
                                                    <div className="p-2.5 bg-orange-500 rounded-lg text-white shadow-sm shadow-orange-500/20">
                                                        <IconQrcode size={20} className="text-white" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 text-right min-w-0">
                                                    <h5 className="font-bold uppercase text-gray-500 dark:text-slate-400 text-xs truncate">Split QRIS</h5>
                                                    <h3 className="font-bold text-base text-slate-800 dark:text-slate-100 truncate mt-0.5">
                                                        Rp {numberWithCommas(splitStats.totalQris)}
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Split Transfer */}
                                        <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow p-[17px] rounded-lg">
                                            <div className="flex flex-row items-center">
                                                <div className="flex-shrink pr-3">
                                                    <div className="p-2.5 bg-blue-600 rounded-lg text-white shadow-sm shadow-blue-600/20">
                                                        <IconBuildingBank size={20} className="text-white" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 text-right min-w-0">
                                                    <h5 className="font-bold uppercase text-gray-500 dark:text-slate-400 text-xs truncate">Split Transfer</h5>
                                                    <h3 className="font-bold text-base text-slate-800 dark:text-slate-100 truncate mt-0.5">
                                                        Rp {numberWithCommas(splitStats.totalTransfer)}
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Split EDC */}
                                        <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow p-[17px] rounded-lg">
                                            <div className="flex flex-row items-center">
                                                <div className="flex-shrink pr-3">
                                                    <div className="p-2.5 bg-purple-600 rounded-lg text-white shadow-sm shadow-purple-600/20">
                                                        <IconCreditCard size={20} className="text-white" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 text-right min-w-0">
                                                    <h5 className="font-bold uppercase text-gray-500 dark:text-slate-400 text-xs truncate">Split EDC</h5>
                                                    <h3 className="font-bold text-base text-slate-800 dark:text-slate-100 truncate mt-0.5">
                                                        Rp {numberWithCommas(splitStats.totalEdc)}
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action & Filter Bar */}
                                    <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-xs flex flex-col md:flex-row items-center justify-between gap-3">
                                        <div className="flex flex-1 flex-col sm:flex-row items-center gap-2.5 w-full">
                                            {/* Search */}
                                            <div className="relative w-full sm:max-w-xs">
                                                <input
                                                    type="text"
                                                    placeholder="Cari kode barang, nama, kasir..."
                                                    value={searchKeyword}
                                                    onChange={(e) => {
                                                        setSearchKeyword(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                    className="w-full pl-9 pr-3.5 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 dark:focus:border-slate-100"
                                                />
                                                <IconSearch size={16} className="absolute left-3 top-2.5 text-slate-400" />
                                            </div>

                                            {/* Filter Metode */}
                                            <div className="flex items-center gap-1.5 w-full sm:w-auto">
                                                <IconFilter size={16} className="text-slate-400 shrink-0" />
                                                <select
                                                    value={metodeFilter}
                                                    onChange={(e) => {
                                                        setMetodeFilter(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                    className="select select-bordered select-sm text-xs rounded-xl dark:bg-slate-900 dark:border-slate-700 dark:text-white w-full sm:w-auto h-[36px]"
                                                >
                                                    <option value="all">Semua Metode Split</option>
                                                    <option value="cash">Mengandung CASH</option>
                                                    <option value="qris">Mengandung QRIS</option>
                                                    <option value="transfer">Mengandung Transfer</option>
                                                    <option value="edc">Mengandung EDC</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Data Table */}
                                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-xs overflow-hidden">
                                        <div className="overflow-x-auto scrollbar-thin">
                                            <table className="min-w-full text-left text-xs">
                                                <thead>
                                                    <tr className="bg-slate-100 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 uppercase tracking-wider font-bold">
                                                        <th className="py-3 px-3 text-center w-12">No</th>
                                                        <th className="py-3 px-3.5">Kode Barang</th>
                                                        <th className="py-3 px-3.5">Nama Barang</th>
                                                        <th className="py-3 px-3 text-center">Karat</th>
                                                        <th className="py-3 px-3 text-right">Berat</th>
                                                        <th className="py-3 px-3.5 text-right font-extrabold text-slate-900 dark:text-white">Total Harga</th>
                                                        <th className="py-3 px-3.5 text-right text-slate-900 dark:text-white font-extrabold">CASH</th>
                                                        <th className="py-3 px-3.5 text-right text-slate-900 dark:text-white font-extrabold">QRIS</th>
                                                        <th className="py-3 px-3.5 text-right text-slate-900 dark:text-white font-extrabold">TRANSFER</th>
                                                        <th className="py-3 px-3.5 text-right text-slate-900 dark:text-white font-extrabold">EDC</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                                                    {paginatedSplitData.length > 0 ? (
                                                        paginatedSplitData.map((trx, index) => {
                                                            const rowNo = (currentPage - 1) * pageSize + index + 1;
                                                            return (
                                                                <tr
                                                                    key={trx.id || index}
                                                                    onClick={() => {
                                                                        setSelectedSplitTrx(trx);
                                                                        setIsOpenDetailModal(true);
                                                                    }}
                                                                    className="hover:bg-amber-50/60 dark:hover:bg-slate-700/40 transition-colors cursor-pointer group"
                                                                    title="Klik untuk melihat detail transaksi"
                                                                >
                                                                    {/* No */}
                                                                    <td className="py-3 px-3 text-center text-slate-400 font-medium">
                                                                        {rowNo}
                                                                    </td>

                                                                    {/* Kode Barang */}
                                                                    <td className="py-3 px-3.5 whitespace-nowrap">
                                                                        <span className="font-bold text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-600 group-hover:border-amber-400 transition-colors">
                                                                            {trx.item?.kode_item || '-'}
                                                                        </span>
                                                                    </td>

                                                                    {/* Nama Barang */}
                                                                    <td className="py-3 px-3.5 whitespace-nowrap font-medium text-slate-800 dark:text-slate-200">
                                                                        {trx.item?.nama_barang || '-'}
                                                                    </td>

                                                                    {/* Karat (tanpa ikon) */}
                                                                    <td className="py-3 px-3 text-center whitespace-nowrap font-bold text-slate-700 dark:text-slate-300">
                                                                        {trx.item?.karat || 0}K
                                                                    </td>

                                                                    {/* Berat (tanpa ikon) */}
                                                                    <td className="py-3 px-3 text-right whitespace-nowrap font-bold text-slate-700 dark:text-slate-300">
                                                                        {checkBerat(trx.item?.berat)} gr
                                                                    </td>

                                                                    {/* Total Harga */}
                                                                    <td className="py-3 px-3.5 text-right whitespace-nowrap font-extrabold text-slate-900 dark:text-white bg-slate-50/50 dark:bg-slate-800/40">
                                                                        Rp {numberWithCommas(trx.total_harga || trx.harga_jual || 0)}
                                                                    </td>

                                                                    {/* CASH */}
                                                                    <td className="py-3 px-3.5 text-right whitespace-nowrap font-bold">
                                                                        {trx.cash > 0 ? (
                                                                            <span className="text-slate-900 dark:text-white">
                                                                                Rp {numberWithCommas(trx.cash)}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-slate-300 dark:text-slate-600">-</span>
                                                                        )}
                                                                    </td>

                                                                    {/* QRIS */}
                                                                    <td className="py-3 px-3.5 text-right whitespace-nowrap font-bold">
                                                                        {trx.qris > 0 ? (
                                                                            <span className="text-slate-900 dark:text-white">
                                                                                Rp {numberWithCommas(trx.qris)}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-slate-300 dark:text-slate-600">-</span>
                                                                        )}
                                                                    </td>

                                                                    {/* TRANSFER */}
                                                                    <td className="py-3 px-3.5 text-right whitespace-nowrap font-bold">
                                                                        {trx.transfer > 0 ? (
                                                                            <div>
                                                                                <span className="text-slate-900 dark:text-white">
                                                                                    Rp {numberWithCommas(trx.transfer)}
                                                                                </span>
                                                                                {trx.transfer_bank && (
                                                                                    <div className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold">
                                                                                        ({trx.transfer_bank})
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-slate-300 dark:text-slate-600">-</span>
                                                                        )}
                                                                    </td>

                                                                    {/* EDC */}
                                                                    <td className="py-3 px-3.5 text-right whitespace-nowrap font-bold">
                                                                        {trx.edc > 0 ? (
                                                                            <div>
                                                                                <span className="text-slate-900 dark:text-white">
                                                                                    Rp {numberWithCommas(trx.edc)}
                                                                                </span>
                                                                                {trx.edc_bank && (
                                                                                    <div className="text-[10px] text-slate-500 dark:text-slate-400 font-semibold">
                                                                                        ({trx.edc_bank})
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-slate-300 dark:text-slate-600">-</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={10} className="py-14 text-center text-slate-400">
                                                                <div className="flex flex-col items-center justify-center gap-2">
                                                                    <IconArrowsSplit size={36} className="text-slate-300 dark:text-slate-600" />
                                                                    <p className="font-semibold text-slate-600 dark:text-slate-300">
                                                                        Tidak ada transaksi dengan split pembayaran.
                                                                    </p>
                                                                    <p className="text-xs text-slate-400">
                                                                        Coba ganti filter cabang, rentang tanggal, atau kata kunci pencarian.
                                                                    </p>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>

                                                {/* Table Footer - Total Accumulation */}
                                                {paginatedSplitData.length > 0 && (
                                                    <tfoot>
                                                        <tr className="bg-slate-100/80 dark:bg-slate-900 border-t-2 border-slate-200 dark:border-slate-700 font-bold text-slate-800 dark:text-slate-100">
                                                            <td colSpan={5} className="py-3 px-4 text-right uppercase tracking-wider text-xs">
                                                                TOTAL ({filteredSplitData.length} Trx Terfilter) :
                                                            </td>
                                                            <td className="py-3 px-3.5 text-right text-xs sm:text-sm text-slate-900 dark:text-white font-black bg-slate-200/40 dark:bg-slate-800/60">
                                                                Rp {numberWithCommas(splitStats.totalNilai)}
                                                            </td>
                                                            <td className="py-3 px-3.5 text-right text-xs sm:text-sm text-slate-900 dark:text-white font-extrabold">
                                                                Rp {numberWithCommas(splitStats.totalCash)}
                                                            </td>
                                                            <td className="py-3 px-3.5 text-right text-xs sm:text-sm text-slate-900 dark:text-white font-extrabold">
                                                                Rp {numberWithCommas(splitStats.totalQris)}
                                                            </td>
                                                            <td className="py-3 px-3.5 text-right text-xs sm:text-sm text-slate-900 dark:text-white font-extrabold">
                                                                Rp {numberWithCommas(splitStats.totalTransfer)}
                                                            </td>
                                                            <td className="py-3 px-3.5 text-right text-xs sm:text-sm text-slate-900 dark:text-white font-extrabold">
                                                                Rp {numberWithCommas(splitStats.totalEdc)}
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                )}
                                            </table>
                                        </div>

                                        {/* Pagination Footer */}
                                        {filteredSplitData.length > 0 && (
                                            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 flex flex-col sm:flex-row items-center justify-between gap-3">
                                                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                                    <span>Tampilkan</span>
                                                    <select
                                                        value={pageSize}
                                                        onChange={(e) => {
                                                            setPageSize(Number(e.target.value));
                                                            setCurrentPage(1);
                                                        }}
                                                        className="select select-bordered select-xs rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={15}>15</option>
                                                        <option value={25}>25</option>
                                                        <option value={50}>50</option>
                                                        <option value={100}>100</option>
                                                    </select>
                                                    <span>baris per halaman (Total {filteredSplitData.length} data)</span>
                                                </div>

                                                <div className="flex items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                        disabled={currentPage <= 1}
                                                        className="btn btn-xs btn-outline dark:border-slate-600 dark:text-slate-300 disabled:opacity-30 rounded-lg px-2"
                                                    >
                                                        <IconChevronLeft size={14} />
                                                        <span>Prev</span>
                                                    </button>
                                                    <span className="text-xs px-2 font-semibold text-slate-700 dark:text-slate-300">
                                                        Hal {currentPage} dari {totalPages}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                        disabled={currentPage >= totalPages}
                                                        className="btn btn-xs btn-outline dark:border-slate-600 dark:text-slate-300 disabled:opacity-30 rounded-lg px-2"
                                                    >
                                                        <span>Next</span>
                                                        <IconChevronRight size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* MODAL PROSES PEMBAYARAN DIGITAL KE PENGELUARAN LUAR (CASHFLOW) */}
            <Transition appear show={isOpenProsesModal} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-50"
                    onClose={loadingProses ? () => {} : () => setIsOpenProsesModal(false)}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-xs" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white dark:bg-slate-900 p-6 text-left align-middle shadow-2xl transition-all border border-slate-200 dark:border-slate-800">
                                    {/* Modal Header */}
                                    <div className="flex items-start justify-between gap-3 pb-4 border-b border-slate-100 dark:border-slate-800">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                                                <IconArrowsRightLeft size={22} />
                                            </div>
                                            <div>
                                                <Dialog.Title className="text-base md:text-lg font-extrabold text-slate-800 dark:text-white">
                                                    Proses Pembayaran ke Pengeluaran Luar
                                                </Dialog.Title>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                    Kirim nominal pembayaran Transfer, QRIS, dan EDC ke transaksi Cashflow (Pengeluaran Luar)
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setIsOpenProsesModal(false)}
                                            disabled={loadingProses}
                                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                                        >
                                            <IconX size={18} />
                                        </button>
                                    </div>

                                    <form onSubmit={handleSubmitProsesCashflow} className="mt-4 space-y-4">
                                        {/* Pilihan Cabang untuk Admin / Owner */}
                                        {(role === 'admin' || role === 'owner') && cabangList.length > 0 && (
                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5 uppercase tracking-wider">
                                                    Cabang / Toko Tujuan Cashflow
                                                </label>
                                                <select
                                                    value={prosesCabangId}
                                                    onChange={(e) => handleChangeProsesCabang(e.target.value)}
                                                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-white text-xs font-semibold focus:border-slate-900 dark:focus:border-slate-100 focus:outline-none"
                                                    required
                                                >
                                                    <option value="">-- Pilih Cabang / Toko --</option>
                                                    {cabangList.map(cab => (
                                                        <option key={cab.id} value={cab.id}>{cab.nama_cabang}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {allDigitalSynced && (
                                            <div className="p-3 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 text-xs flex items-center gap-2">
                                                <IconCheck size={16} className="shrink-0 text-slate-900 dark:text-white" />
                                                <span>Semua nominal metode pembayaran digital pada periode dan cabang ini sudah sinkron di Pengeluaran Luar.</span>
                                            </div>
                                        )}

                                        {/* Daftar Metode Pembayaran Digital */}
                                        <div className="space-y-3">
                                            {/* 1. Digital Pay Transfer */}
                                            {(() => {
                                                const nomTransfer = Number(summary?.transfer?.total || 0);
                                                const status = methodStatus.transfer;
                                                const disabled = nomTransfer <= 0 || status.isSynced;
                                                const isChecked = Boolean(prosesSelection.transfer && !disabled);
                                                return (
                                                    <div
                                                        className={`p-4 rounded-xl border transition-all ${
                                                            status.isSynced
                                                                ? 'border-slate-200 bg-slate-100/60 dark:border-slate-700 dark:bg-slate-800/40'
                                                                : isChecked
                                                                ? 'border-slate-900 bg-slate-50 dark:border-slate-400 dark:bg-slate-800/60 shadow-xs'
                                                                : 'border-slate-200 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-800/40 opacity-70'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between gap-3">
                                                            <label className={`flex items-center gap-3 flex-1 min-w-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    disabled={disabled}
                                                                    onChange={(e) =>
                                                                        setProsesSelection(prev => ({ ...prev, transfer: e.target.checked }))
                                                                    }
                                                                    className="checkbox checkbox-sm rounded-md disabled:opacity-40"
                                                                />
                                                                <div className="w-9 h-9 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                                                                    <IconBuildingBank size={18} />
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className="font-extrabold text-slate-900 dark:text-white text-xs sm:text-sm">
                                                                            Digital Pay Transfer
                                                                        </span>
                                                                        {status.isSynced ? (
                                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
                                                                                ✓ Sudah Diproses (Sinkron)
                                                                            </span>
                                                                        ) : status.needsUpdate ? (
                                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100">
                                                                                Perlu Update (Rp {numberWithCommas(status.existingNominal)} → Rp {numberWithCommas(nomTransfer)})
                                                                            </span>
                                                                        ) : status.isProcessed ? (
                                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100">
                                                                                Sudah Ada di Cashflow
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-100 text-slate-900 border border-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600">
                                                                                Record Baru
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                                                        {transferBankEntries.length} Bank ({summary?.counts?.transfer || 0} Transaksi)
                                                                        {status.isSynced && (
                                                                            <span className="ml-1 text-slate-700 dark:text-slate-300 font-medium">
                                                                                • Sudah tercatat di Pengeluaran Luar
                                                                            </span>
                                                                        )}
                                                                        {status.needsUpdate && (
                                                                            <span className="ml-1 text-slate-700 dark:text-slate-300 font-medium">
                                                                                • Akan mengupdate record ID #{status.record?.id} tanpa duplikasi
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            </label>
                                                            <div className="text-right shrink-0">
                                                                <span className="font-black text-sm sm:text-base text-slate-900 dark:text-white">
                                                                    Rp {numberWithCommas(nomTransfer)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {isChecked && (
                                                            <div className="mt-2.5 pl-9">
                                                                <input
                                                                    type="text"
                                                                    value={prosesKeterangan.transfer}
                                                                    onChange={(e) =>
                                                                        setProsesKeterangan(prev => ({ ...prev, transfer: e.target.value }))
                                                                    }
                                                                    placeholder="Keterangan Digital Pay Transfer..."
                                                                    className="w-full px-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:border-slate-900 dark:focus:border-slate-100"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            {/* 2. Digital Pay QRIS */}
                                            {(() => {
                                                const nomQris = Number(summary?.qris || 0);
                                                const status = methodStatus.qris;
                                                const disabled = nomQris <= 0 || status.isSynced;
                                                const isChecked = Boolean(prosesSelection.qris && !disabled);
                                                return (
                                                    <div
                                                        className={`p-4 rounded-xl border transition-all ${
                                                            status.isSynced
                                                                ? 'border-slate-200 bg-slate-100/60 dark:border-slate-700 dark:bg-slate-800/40'
                                                                : isChecked
                                                                ? 'border-slate-900 bg-slate-50 dark:border-slate-400 dark:bg-slate-800/60 shadow-xs'
                                                                : 'border-slate-200 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-800/40 opacity-70'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between gap-3">
                                                            <label className={`flex items-center gap-3 flex-1 min-w-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    disabled={disabled}
                                                                    onChange={(e) =>
                                                                        setProsesSelection(prev => ({ ...prev, qris: e.target.checked }))
                                                                    }
                                                                    className="checkbox checkbox-sm rounded-md disabled:opacity-40"
                                                                />
                                                                <div className="w-9 h-9 rounded-lg bg-orange-500/10 text-orange-600 dark:text-orange-400 flex items-center justify-center shrink-0">
                                                                    <IconQrcode size={18} />
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className="font-extrabold text-slate-900 dark:text-white text-xs sm:text-sm">
                                                                            Digital Pay QRIS
                                                                        </span>
                                                                        {status.isSynced ? (
                                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
                                                                                ✓ Sudah Diproses (Sinkron)
                                                                            </span>
                                                                        ) : status.needsUpdate ? (
                                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100">
                                                                                Perlu Update (Rp {numberWithCommas(status.existingNominal)} → Rp {numberWithCommas(nomQris)})
                                                                            </span>
                                                                        ) : status.isProcessed ? (
                                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100">
                                                                                Sudah Ada di Cashflow
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-100 text-slate-900 border border-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600">
                                                                                Record Baru
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                                                        {summary?.counts?.qris || 0} Transaksi QRIS
                                                                        {status.isSynced && (
                                                                            <span className="ml-1 text-slate-700 dark:text-slate-300 font-medium">
                                                                                • Sudah tercatat di Pengeluaran Luar
                                                                            </span>
                                                                        )}
                                                                        {status.needsUpdate && (
                                                                            <span className="ml-1 text-slate-700 dark:text-slate-300 font-medium">
                                                                                • Akan mengupdate record ID #{status.record?.id} tanpa duplikasi
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            </label>
                                                            <div className="text-right shrink-0">
                                                                <span className="font-black text-sm sm:text-base text-slate-900 dark:text-white">
                                                                    Rp {numberWithCommas(nomQris)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {isChecked && (
                                                            <div className="mt-2.5 pl-9">
                                                                <input
                                                                    type="text"
                                                                    value={prosesKeterangan.qris}
                                                                    onChange={(e) =>
                                                                        setProsesKeterangan(prev => ({ ...prev, qris: e.target.value }))
                                                                    }
                                                                    placeholder="Keterangan Digital Pay QRIS..."
                                                                    className="w-full px-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:border-slate-900 dark:focus:border-slate-100"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            {/* 3. Digital Pay EDC */}
                                            {(() => {
                                                const nomEdc = Number(summary?.edc || 0);
                                                const status = methodStatus.edc;
                                                const disabled = nomEdc <= 0 || status.isSynced;
                                                const isChecked = Boolean(prosesSelection.edc && !disabled);
                                                return (
                                                    <div
                                                        className={`p-4 rounded-xl border transition-all ${
                                                            status.isSynced
                                                                ? 'border-slate-200 bg-slate-100/60 dark:border-slate-700 dark:bg-slate-800/40'
                                                                : isChecked
                                                                ? 'border-slate-900 bg-slate-50 dark:border-slate-400 dark:bg-slate-800/60 shadow-xs'
                                                                : 'border-slate-200 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-800/40 opacity-70'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between gap-3">
                                                            <label className={`flex items-center gap-3 flex-1 min-w-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    disabled={disabled}
                                                                    onChange={(e) =>
                                                                        setProsesSelection(prev => ({ ...prev, edc: e.target.checked }))
                                                                    }
                                                                    className="checkbox checkbox-sm rounded-md disabled:opacity-40"
                                                                />
                                                                <div className="w-9 h-9 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
                                                                    <IconCreditCard size={18} />
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className="font-extrabold text-slate-900 dark:text-white text-xs sm:text-sm">
                                                                            Digital Pay EDC
                                                                        </span>
                                                                        {status.isSynced ? (
                                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
                                                                                ✓ Sudah Diproses (Sinkron)
                                                                            </span>
                                                                        ) : status.needsUpdate ? (
                                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100">
                                                                                Perlu Update (Rp {numberWithCommas(status.existingNominal)} → Rp {numberWithCommas(nomEdc)})
                                                                            </span>
                                                                        ) : status.isProcessed ? (
                                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100">
                                                                                Sudah Ada di Cashflow
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-100 text-slate-900 border border-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600">
                                                                                Record Baru
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                                                        {edcBankEntries.length} Bank ({summary?.counts?.edc || 0} Transaksi)
                                                                        {status.isSynced && (
                                                                            <span className="ml-1 text-slate-700 dark:text-slate-300 font-medium">
                                                                                • Sudah tercatat di Pengeluaran Luar
                                                                            </span>
                                                                        )}
                                                                        {status.needsUpdate && (
                                                                            <span className="ml-1 text-slate-700 dark:text-slate-300 font-medium">
                                                                                • Akan mengupdate record ID #{status.record?.id} tanpa duplikasi
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            </label>
                                                            <div className="text-right shrink-0">
                                                                <span className="font-black text-sm sm:text-base text-slate-900 dark:text-white">
                                                                    Rp {numberWithCommas(nomEdc)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {isChecked && (
                                                            <div className="mt-2.5 pl-9">
                                                                <input
                                                                    type="text"
                                                                    value={prosesKeterangan.edc}
                                                                    onChange={(e) =>
                                                                        setProsesKeterangan(prev => ({ ...prev, edc: e.target.value }))
                                                                    }
                                                                    placeholder="Keterangan Digital Pay EDC..."
                                                                    className="w-full px-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:border-slate-900 dark:focus:border-slate-100"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        {/* Summary Total yang Dipilih */}
                                        <div className="p-3.5 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                                Total Diproses ke Pengeluaran Luar :
                                            </span>
                                            <span className="text-base font-black text-slate-900 dark:text-white">
                                                Rp {numberWithCommas(totalDigitalPaySelected)}
                                            </span>
                                        </div>

                                        {/* Modal Actions */}
                                        <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                                            <button
                                                type="button"
                                                onClick={() => navigate('/pengeluaran')}
                                                className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition"
                                            >
                                                <IconExternalLink size={15} />
                                                <span>Lihat Halaman Pengeluaran Luar</span>
                                            </button>

                                            <div className="flex items-center gap-2 ml-auto">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsOpenProsesModal(false)}
                                                    disabled={loadingProses}
                                                    className="px-4 py-2 text-xs font-bold text-slate-700 dark:text-slate-300 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl transition"
                                                >
                                                    Batal
                                                </button>
                                                <button
                                                    type="submit"
                                                    disabled={loadingProses || totalDigitalPaySelected <= 0}
                                                    className="px-5 py-2 text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 active:bg-amber-700 rounded-xl shadow-sm shadow-amber-500/20 transition flex items-center gap-1.5 disabled:opacity-50"
                                                >
                                                    {loadingProses ? (
                                                        <>
                                                            <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                            <span>Memproses...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <IconArrowsRightLeft size={15} />
                                                            <span>Proses Sekarang</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* MODAL DETAIL TRANSAKSI SPLIT DARI KANAN LAYAR */}
            <Transition appear show={isOpenDetailModal} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-50"
                    onClose={() => setIsOpenDetailModal(false)}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-xs" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-hidden pointer-events-none">
                        <div className="fixed inset-y-0 right-0 flex max-w-full pl-6 sm:pl-10">
                            <Transition.Child
                                as={Fragment}
                                enter="transform transition ease-in-out duration-300"
                                enterFrom="translate-x-full"
                                enterTo="translate-x-0"
                                leave="transform transition ease-in-out duration-300"
                                leaveFrom="translate-x-0"
                                leaveTo="translate-x-full"
                            >
                                <Dialog.Panel className="pointer-events-auto w-screen max-w-md sm:max-w-lg bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col h-full">
                                    {/* Header Drawer */}
                                    <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between gap-3 bg-slate-50/50 dark:bg-slate-800/40 shrink-0">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                                                <IconArrowsSplit size={20} />
                                            </div>
                                            <div>
                                                <Dialog.Title className="text-base sm:text-lg font-extrabold text-slate-800 dark:text-white">
                                                    Detail Transaksi Split
                                                </Dialog.Title>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                    Kode Faktur: <span className="font-bold text-amber-600 dark:text-amber-400">{selectedSplitTrx?.kode_trx || '-'}</span>
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setIsOpenDetailModal(false)}
                                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                                            title="Tutup"
                                        >
                                            <IconX size={20} />
                                        </button>
                                    </div>

                                    {/* Body Drawer (Scrollable) */}
                                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                        {/* Card 1: Waktu & Lokasi Transaksi (Waktu, Tanggal, Kasir, Cabang) */}
                                        <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/80 dark:border-slate-700/80 p-4 space-y-3">
                                            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                                Waktu & Lokasi Transaksi
                                            </h4>
                                            <div className="grid grid-cols-2 gap-3 text-xs">
                                                <div>
                                                    <span className="text-slate-400 block text-[11px]">Waktu / Tanggal</span>
                                                    <span className="font-bold text-slate-800 dark:text-slate-200 mt-0.5 block">
                                                        {selectedSplitTrx?.created_at || '-'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-400 block text-[11px]">Kode Transaksi</span>
                                                    <span className="font-bold text-slate-800 dark:text-slate-200 mt-0.5 block">
                                                        {selectedSplitTrx?.kode_trx || '-'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-400 block text-[11px]">Kasir</span>
                                                    <span className="font-bold text-slate-800 dark:text-slate-200 mt-0.5 block">
                                                        {selectedSplitTrx?.kasir || '-'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-400 block text-[11px]">Cabang / Toko</span>
                                                    <span className="font-bold text-slate-800 dark:text-slate-200 mt-0.5 block">
                                                        {selectedSplitTrx?.cabang || '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card 2: Informasi Barang */}
                                        <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/80 dark:border-slate-700/80 p-4 space-y-3">
                                            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                                Informasi Barang Terjual
                                            </h4>
                                            <div className="space-y-2 text-xs">
                                                <div className="flex items-center justify-between pb-2 border-b border-slate-200/60 dark:border-slate-700/60">
                                                    <span className="text-slate-500 dark:text-slate-400">Kode Barang</span>
                                                    <span className="font-bold px-2 py-0.5 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white">
                                                        {selectedSplitTrx?.item?.kode_item || '-'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between pb-2 border-b border-slate-200/60 dark:border-slate-700/60">
                                                    <span className="text-slate-500 dark:text-slate-400">Nama Barang</span>
                                                    <span className="font-bold text-slate-900 dark:text-white text-right">
                                                        {selectedSplitTrx?.item?.nama_barang || '-'}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3 pt-1">
                                                    <div>
                                                        <span className="text-slate-400 block text-[11px]">Karat</span>
                                                        <span className="font-extrabold text-slate-800 dark:text-slate-200 mt-0.5 block">
                                                            {selectedSplitTrx?.item?.karat || 0}K
                                                        </span>
                                                    </div>
                                                    <div className="text-right">
                                                        <span className="text-slate-400 block text-[11px]">Berat</span>
                                                        <span className="font-extrabold text-slate-800 dark:text-slate-200 mt-0.5 block">
                                                            {checkBerat(selectedSplitTrx?.item?.berat)} gr
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="pt-2 border-t border-slate-200/60 dark:border-slate-700/60 flex items-center justify-between">
                                                    <span className="font-bold text-slate-700 dark:text-slate-300">Total Harga Barang</span>
                                                    <span className="font-black text-sm text-slate-900 dark:text-white">
                                                        Rp {numberWithCommas(selectedSplitTrx?.total_harga || selectedSplitTrx?.harga_jual || 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card 3: Rincian Split Pembayaran */}
                                        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/80 dark:border-slate-700/80 p-4 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                                    Rincian Split Pembayaran
                                                </h4>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsKoreksiPaymentOpen(true)}
                                                        className="px-2 py-0.5 rounded-lg text-xs font-bold bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-300/60 dark:border-amber-700/60 flex items-center gap-1 transition-all cursor-pointer shadow-2xs"
                                                        title="Koreksi / Ubah Metode Pembayaran Transaksi Ini"
                                                    >
                                                        <IconEdit size={13} />
                                                        <span>Koreksi</span>
                                                    </button>
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border border-indigo-200/80 dark:border-indigo-800/50">
                                                        {(selectedSplitTrx?.payments || []).length > 0 ? `${selectedSplitTrx.payments.length} Metode` : 'Split Pay'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                {/* Detail Payments List */}
                                                {(selectedSplitTrx?.payments && selectedSplitTrx.payments.length > 0) ? (
                                                    selectedSplitTrx.payments.map((p, pIdx) => {
                                                        const m = (p.metode_pembayaran || '').toLowerCase();
                                                        let icon = <IconReceipt2 size={16} />;
                                                        let badgeBg = 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200';

                                                        if (m.includes('cash') || m.includes('tunai')) {
                                                            icon = <IconCash size={16} />;
                                                            badgeBg = 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800';
                                                        } else if (m.includes('qris')) {
                                                            icon = <IconQrcode size={16} />;
                                                            badgeBg = 'bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800';
                                                        } else if (m.includes('transfer')) {
                                                            icon = <IconBuildingBank size={16} />;
                                                            badgeBg = 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800';
                                                        } else if (m.includes('edc') || m.includes('debit')) {
                                                            icon = <IconCreditCard size={16} />;
                                                            badgeBg = 'bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800';
                                                        }

                                                        return (
                                                            <div
                                                                key={pIdx}
                                                                className="flex items-center justify-between p-3 rounded-xl border border-slate-100 dark:border-slate-700/60 bg-slate-50/60 dark:bg-slate-900/40"
                                                            >
                                                                <div className="flex items-center gap-2.5 min-w-0">
                                                                    <div className={`p-1.5 rounded-lg shrink-0 ${badgeBg}`}>
                                                                        {icon}
                                                                    </div>
                                                                    <div className="min-w-0">
                                                                        <span className="font-bold text-xs text-slate-800 dark:text-slate-200 block truncate">
                                                                            {(p.metode_pembayaran || '').toUpperCase()}
                                                                        </span>
                                                                        {p.nama_bank && (
                                                                            <span className="text-[11px] text-slate-400 block truncate">
                                                                                Bank: {p.nama_bank}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <span className="font-extrabold text-xs sm:text-sm text-slate-900 dark:text-white shrink-0">
                                                                    Rp {numberWithCommas(p.nominal)}
                                                                </span>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    /* Fallback directly from transaction fields if payments array empty */
                                                    <div className="space-y-2">
                                                        {selectedSplitTrx?.cash > 0 && (
                                                            <div className="flex items-center justify-between p-3 rounded-xl border border-slate-100 dark:border-slate-700/60 bg-slate-50/60 dark:bg-slate-900/40">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800">
                                                                        <IconCash size={16} />
                                                                    </div>
                                                                    <span className="font-bold text-xs text-slate-800 dark:text-slate-200">CASH</span>
                                                                </div>
                                                                <span className="font-extrabold text-xs sm:text-sm text-slate-900 dark:text-white">
                                                                    Rp {numberWithCommas(selectedSplitTrx.cash)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {selectedSplitTrx?.qris > 0 && (
                                                            <div className="flex items-center justify-between p-3 rounded-xl border border-slate-100 dark:border-slate-700/60 bg-slate-50/60 dark:bg-slate-900/40">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="p-1.5 rounded-lg bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800">
                                                                        <IconQrcode size={16} />
                                                                    </div>
                                                                    <span className="font-bold text-xs text-slate-800 dark:text-slate-200">QRIS</span>
                                                                </div>
                                                                <span className="font-extrabold text-xs sm:text-sm text-slate-900 dark:text-white">
                                                                    Rp {numberWithCommas(selectedSplitTrx.qris)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {selectedSplitTrx?.transfer > 0 && (
                                                            <div className="flex items-center justify-between p-3 rounded-xl border border-slate-100 dark:border-slate-700/60 bg-slate-50/60 dark:bg-slate-900/40">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="p-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800">
                                                                        <IconBuildingBank size={16} />
                                                                    </div>
                                                                    <div>
                                                                        <span className="font-bold text-xs text-slate-800 dark:text-slate-200 block">TRANSFER</span>
                                                                        {selectedSplitTrx.transfer_bank && (
                                                                            <span className="text-[11px] text-slate-400 block">Bank: {selectedSplitTrx.transfer_bank}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <span className="font-extrabold text-xs sm:text-sm text-slate-900 dark:text-white">
                                                                    Rp {numberWithCommas(selectedSplitTrx.transfer)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {selectedSplitTrx?.edc > 0 && (
                                                            <div className="flex items-center justify-between p-3 rounded-xl border border-slate-100 dark:border-slate-700/60 bg-slate-50/60 dark:bg-slate-900/40">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="p-1.5 rounded-lg bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800">
                                                                        <IconCreditCard size={16} />
                                                                    </div>
                                                                    <div>
                                                                        <span className="font-bold text-xs text-slate-800 dark:text-slate-200 block">EDC</span>
                                                                        {selectedSplitTrx.edc_bank && (
                                                                            <span className="text-[11px] text-slate-400 block">Bank: {selectedSplitTrx.edc_bank}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <span className="font-extrabold text-xs sm:text-sm text-slate-900 dark:text-white">
                                                                    Rp {numberWithCommas(selectedSplitTrx.edc)}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Total Split Accumulation */}
                                                <div className="p-3.5 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 flex items-center justify-between mt-3">
                                                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                                        Total Terbayar (Split) :
                                                    </span>
                                                    <span className="text-base font-black text-slate-900 dark:text-white">
                                                        Rp {numberWithCommas(selectedSplitTrx?.total_harga || selectedSplitTrx?.harga_jual || 0)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Footer Drawer */}
                                    <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 flex items-center justify-between shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => setIsKoreksiPaymentOpen(true)}
                                            className="px-4 py-2 text-xs font-bold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700/70 hover:bg-amber-100 dark:hover:bg-amber-900/50 rounded-xl transition shadow-xs cursor-pointer flex items-center gap-1.5"
                                        >
                                            <IconEdit size={15} />
                                            <span>Koreksi Metode Bayar</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setIsOpenDetailModal(false)}
                                            className="px-5 py-2 text-xs font-bold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition shadow-xs cursor-pointer"
                                        >
                                            Tutup
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Modal Koreksi Metode Pembayaran */}
            <ModalKoreksiPembayaran
                isOpen={isKoreksiPaymentOpen}
                onClose={() => setIsKoreksiPaymentOpen(false)}
                penjualan={selectedSplitTrx}
                onSuccess={(updatedData) => {
                    setSelectedSplitTrx((prev) => ({
                        ...prev,
                        ...updatedData,
                        payments: updatedData.payments || prev?.payments,
                    }));
                    fetchData();
                }}
            />
        </Layouts>
    );
}
